import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import io from "socket.io-client";
import { apiFetch } from "./AuthContext";
import { useProfile } from "../hooks/useProfile";
import { extractNotificationData, buildNotificationMessage, resolveSenderNames, localizeStoredArabicText } from "../utils/notificationUtils";
import { LanguageContext } from "../constant/LanguageContext";
import { isPersonalNotification } from "../utils/notificationAudience";

const NotificationContext = createContext();
const BASE_URL = process.env.REACT_APP_BASE_URL;
const SOCKET_URL = process.env.REACT_APP_BASE_SERVER_URL || "https://nexploy-eg.com";

// ── Module-level presence cache ──────────────────────────────────────────────
// Backend emits `users:online` as a SNAPSHOT exactly once per socket connect.
// The NotificationContext socket connects at app boot, so when the messenger
// page (or any other consumer) mounts LATER, the snapshot is already past.
// Cache it here, kept current by the listeners installed in the socket
// useEffect below. Late-mounting consumers can read it synchronously.
const presenceCache = new Set();

/** Read the current online-users snapshot (a fresh copy). */
export function getPresenceSnapshot() {
  return new Set(presenceCache);
}

/** True if the given infinityCode is in the latest snapshot. */
export function isUserOnline(infinityCode) {
  if (infinityCode == null) return false;
  return presenceCache.has(String(infinityCode));
}

/**
 * Truncate a full name to only the first two parts.
 * e.g. "Mohamed Ahmed Kamal Zaki" → "Mohamed Ahmed"
 * e.g. "محمد أحمد كمال" → "محمد أحمد"
 */
const truncateToTwoNames = (name) => {
  if (!name) return name;
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).join(' ');
};

export const NotificationProvider = ({ children }) => {
  const { profile, profileLoading } = useProfile();
  const { language } = useContext(LanguageContext);
  // The inbox belongs to the USER, not to the active View: every row addressed
  // to this account is listed and counted the same in User, Manager and Admin
  // View. The View only matters when a notification is clicked — see
  // resolveNotificationNavigation (utils/notificationNavigation.js).

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, hasMore: false, totalPages: 1 });
  // True once the first list fetch succeeded — the dropdown shows skeletons
  // only before that, never on the background refresh that runs on open.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isDropdownOpenRef = useRef(false);

  const [notificationsDisabled, setNotificationsDisabled] = useState(() => {
    return localStorage.getItem("notificationsDisabled") === "true";
  });

  // Same show/hide-a-feature toggle pattern as notificationsDisabled above,
  // for the Mini Chat Dock's Settings entry. Lives here (not in
  // MiniChatContext) because this provider wraps the whole app — including
  // every page under <Routes> — while MiniChatProvider only wraps the dock
  // itself, so a Settings page couldn't otherwise read/write it.
  const [miniChatDisabled, setMiniChatDisabled] = useState(() => {
    return localStorage.getItem("miniChatDisabled") === "true";
  });
  useEffect(() => {
    localStorage.setItem("miniChatDisabled", miniChatDisabled);
  }, [miniChatDisabled]);

  // Show/hide toggle for the floating company badge (ClientBadge). Persisted
  // per user on the server (/api/ui-preferences) because logout wipes
  // localStorage — the local copy is only a cache to avoid a flash on reload.
  const [clientBadgeDisabled, setClientBadgeDisabledState] = useState(() => {
    return localStorage.getItem("clientBadgeDisabled") === "true";
  });
  useEffect(() => {
    localStorage.setItem("clientBadgeDisabled", clientBadgeDisabled);
  }, [clientBadgeDisabled]);

  const setClientBadgeDisabled = useCallback((value) => {
    const next = Boolean(value);
    setClientBadgeDisabledState(next);
    apiFetch(`${BASE_URL}/api/ui-preferences`, {
      method: "PATCH",
      body: JSON.stringify({ prefs: { clientBadgeDisabled: next } }),
    }).catch((err) => console.warn("⚠️ Failed to save UI preferences:", err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadUiPrefs = async () => {
      if (!localStorage.getItem("token")) return;
      try {
        const res = await apiFetch(`${BASE_URL}/api/ui-preferences`, { method: "GET" });
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled) setClientBadgeDisabledState(body?.data?.clientBadgeDisabled === true);
      } catch (err) {
        console.warn("⚠️ Failed to load UI preferences:", err);
      }
    };
    // A new login must not inherit the previous user's in-memory value.
    const onLogout = () => setClientBadgeDisabledState(false);

    loadUiPrefs();
    window.addEventListener("hrms:auth:login", loadUiPrefs);
    window.addEventListener("hrms:auth:logout", onLogout);
    return () => {
      cancelled = true;
      window.removeEventListener("hrms:auth:login", loadUiPrefs);
      window.removeEventListener("hrms:auth:logout", onLogout);
    };
  }, []);

  // Exposed so other consumers (e.g. the Mini Chat Dock) can reuse THIS
  // socket for emits (send/typing/mark-read) instead of opening a second
  // connection. `isConnected` mirrors the existing connect/disconnect
  // console.log handlers below.
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    localStorage.setItem("notificationsDisabled", notificationsDisabled);
  }, [notificationsDisabled]);

  useEffect(() => {
    isDropdownOpenRef.current = isDropdownOpen;
  }, [isDropdownOpen]);
  
  const socketRef = useRef(null);
  const senderCacheRef = useRef({});

  // ==========================================
  // FETCH / RESOLVE HELPERS
  // ==========================================

  const resolveNamesAndMessages = useCallback(async (rawNotifications) => {
    if (!rawNotifications || rawNotifications.length === 0) return [];
    
    // Resolve sender names for numeric sender IDs — includes every distinct
    // sender in an aggregated chat notification's `senders` list, not just
    // the single `from` field, so buildChatMessage can render all names.
    const idsToResolve = Array.from(
      new Set(
        rawNotifications
          .flatMap((n) => {
            const ids = [n.from ? String(n.from) : null];
            if (Array.isArray(n.senders)) {
              ids.push(...n.senders.map((s) => (s?.infinityCode ? String(s.infinityCode) : null)));
            }
            return ids;
          })
          .filter((v) => v && /^\d+$/.test(v) && !senderCacheRef.current[v])
      )
    );

    if (idsToResolve.length > 0) {
      try {
        await resolveSenderNames(
          idsToResolve,
          senderCacheRef,
          BASE_URL,
          (url) => apiFetch(url, { method: "GET" })
        );
      } catch (e) {
        console.error("Error resolving sender names:", e);
      }
    }

    // Build the final notification objects with localized messages
    return rawNotifications.map(notification => {
      const extracted = extractNotificationData(notification);
      const idStr = extracted.from ? String(extracted.from) : null;
      const cachedNameObj = idStr ? senderCacheRef.current[idStr] : null;
      
      const rawSenderName = cachedNameObj
        ? (language === "ar" && cachedNameObj.ar ? cachedNameObj.ar : cachedNameObj.en || cachedNameObj.ar)
        : extracted.from || (language === "ar" ? "مستخدم" : "User");

      // Show only first two name parts for cleaner display
      const senderName = truncateToTwoNames(rawSenderName);

      const senderForMessage = language === "ar"
        ? truncateToTwoNames(cachedNameObj?.ar || cachedNameObj?.en || senderName)
        : truncateToTwoNames(cachedNameObj?.en || cachedNameObj?.ar || senderName);

      // Resolve a display name for every sender in an aggregated chat
      // notification, so buildChatMessage can render "X and Y sent
      // messages" instead of only ever knowing about a single sender.
      const resolvedSenders = Array.isArray(extracted.senders)
        ? extracted.senders.map((s) => {
            const sIdStr = s?.infinityCode ? String(s.infinityCode) : null;
            const sCached = sIdStr ? senderCacheRef.current[sIdStr] : null;
            const rawName = sCached
              ? (language === "ar" && sCached.ar ? sCached.ar : sCached.en || sCached.ar)
              : (s?.name || s?.infinityCode || senderName);
            return { ...s, resolvedName: truncateToTwoNames(rawName) };
          })
        : extracted.senders;
      const extractedWithSenders = { ...extracted, senders: resolvedSenders };

      // Bilingual notifications (rendered and stored in BOTH languages at
      // creation time on the backend) are picked directly by current
      // language — no regex, no guessing, always correct. Older
      // notifications that predate this, or types that don't need it
      // (chat/calendar/employee-change), fall back to the previous
      // heuristic builders unchanged.
      const isArabic = language === "ar";
      // Chat notifications are always rendered client-side from `senders`:
      // the stored bilingual text bakes in whatever name the sending service
      // had at the time (sometimes a bare infinityCode) plus a call-to-action,
      // while here every sender is resolved to a real display name.
      const isChat = extractedWithSenders.type === "MESSAGE";
      const hasBilingualTitle = !isChat && (extractedWithSenders.titleEn || extractedWithSenders.titleAr);
      const hasBilingualMessage = !isChat && (extractedWithSenders.messageEn || extractedWithSenders.messageAr);

      // Stored Arabic text is tidied on the way out (English step names,
      // day-count agreement) so notifications delivered before the backend
      // localized them read correctly too.
      const message = hasBilingualMessage
        ? (isArabic ? localizeStoredArabicText(extractedWithSenders.messageAr || extractedWithSenders.messageEn) : (extractedWithSenders.messageEn || extractedWithSenders.messageAr))
        : buildNotificationMessage(extractedWithSenders, senderForMessage, language);

      const title = hasBilingualTitle
        ? (isArabic ? (extractedWithSenders.titleAr || extractedWithSenders.titleEn) : (extractedWithSenders.titleEn || extractedWithSenders.titleAr))
        : getTitleForNotification(extractedWithSenders, message, language);

      return {
        ...extractedWithSenders,
        fromName: senderName,
        message,
        title,
        isPersonal: isPersonalNotification(notification),
      };
    });
  }, [language]);

  const getTitleForNotification = (extracted, message, lang) => {
    let title = message || extracted.eventTitle || extracted.originalMessage || "";

    if (extracted.type?.startsWith("LEAVE")) {
      title = extracted.eventTitle || (lang === "ar" ? "طلب إجازة" : "Leave Request");
      if (lang === "ar" && typeof title === "string") {
        if (title.includes("Leave Request Submitted")) {
          title = title.replace("Leave Request Submitted", "تم تقديم طلب الإجازة");
        } else if (title.includes("Leave request") || title.includes("Leave Request")) {
          title = title.replace(/Leave request|Leave Request/i, "طلب الإجازة");
        }
        title = title.replace("Sick Leave", "إجازة مرضية");
        title = title.replace("Annual Leave", "إجازة سنوية");
        title = title.replace("Unpaid Leave", "إجازة بدون أجر");
        title = title.replace("Casual Leave", "إجازة عارضة");
        title = title.replace("Emergency Leave", "إجازة طارئة");
        title = title.replace("Maternity Leave", "إجازة وضع");
        title = title.replace(/Rejected/i, "مرفوض");
        title = title.replace(/Approved/i, "موافق عليها");
      }
      if (lang === "en" && typeof title === "string") {
        if (title.includes("تم تقديم طلب الإجازة")) {
          title = title.replace("تم تقديم طلب الإجازة", "Leave Request Submitted");
        } else if (title.includes("تقديم طلب الإجازة") || title.includes("طلب الإجازة")) {
          title = title.replace(/تقديم طلب الإجازة|طلب الإجازة/g, "Leave Request");
        }
        title = title.replace("إجازة مرضية", "Sick Leave");
        title = title.replace("إجازة سنوية", "Annual Leave");
        title = title.replace("إجازة بدون أجر", "Unpaid Leave");
        title = title.replace("إجازة بدون راتب", "Unpaid Leave");
        title = title.replace("إجازة طارئة", "Emergency Leave");
        title = title.replace("إجازة عارضة", "Casual Leave");
        title = title.replace("إجازة وضع", "Maternity Leave");
      }
    } else if (
      extracted.type === "DOCUMENT_REQUEST" ||
      extracted.eventTitle === "Document Request" ||
      extracted.eventTitle === "طلب مستند"
    ) {
      title = extracted.eventTitle || (lang === "ar" ? "طلب مستند" : "Document Request");
      if (lang === "ar" && typeof title === "string") {
        if (title.includes("Document Request Completed")) {
          title = "طلب مستند مكتمل";
        } else if (title.includes("Document Request Rejected")) {
          title = "رفض طلب المستند";
        } else if (title.includes("Document Request Submitted")) {
          title = "تم تقديم طلب مستند";
        } else if (title.includes("Document Request")) {
          title = "طلب مستند";
        }
      }
      if (lang === "en" && typeof title === "string") {
        if (title.includes("طلب مستند مكتمل") || title.includes("مكتمل")) {
          title = "Document Request Completed";
        } else if (title.includes("رفض طلب المستند") || title.includes("مرفوض")) {
          title = "Document Request Rejected";
        } else if (title.includes("تم تقديم طلب مستند") || title.includes("تم تقديم")) {
          title = "Document Request Submitted";
        } else if (title.includes("طلب مستند")) {
          title = "Document Request";
        }
      }
    } else if (extracted.type?.startsWith("ADVANCEMENT")) {
      const src = (extracted.eventTitle || extracted.originalMessage || "").toLowerCase();
      if (/reject|rejected|رفض/.test(src)) {
        title = lang === "ar" ? "تم رفض طلب السلفه" : "Advancement Request Rejected";
      } else if (/approve|approved|مواف/.test(src)) {
        title = lang === "ar" ? "الموافقة على طلب سلفة" : "Advancement Request Approved";
      } else if (/disburs|disbursed|صرف/.test(src)) {
        title = lang === "ar" ? "تم صرف السلفة" : "Advancement Disbursed";
      } else {
        title = lang === "ar" ? "طلب سلفة" : "Advancement Request";
      }
    } else if (extracted.type === "CALENDAR_EVENT") {
      const calSrc = (extracted.originalMessage || "").toLowerCase();
      if (/declin|رفض/.test(calSrc)) {
        title = lang === "ar" ? "رفض دعوة الحدث" : "Event Invitation Declined";
      } else if (/accept|confirm|قبل|وافق/.test(calSrc)) {
        title = lang === "ar" ? "قبول دعوة الحدث" : "Event Invitation Accepted";
      } else {
        title = extracted.eventTitle || (lang === "ar" ? "دعوة لحدث" : "Event Invitation");
      }
    } else if (extracted.type === "MESSAGE" || extracted.messageId) {
      // The sender line ("New message from X") IS the notification — use it
      // as the title; the item hides a message identical to its title, so
      // nothing is shown twice.
      title = message || (lang === "ar" ? "رسائل جديدة" : "New Messages");
    } else if (title && title.length > 50) {
      title = title.substring(0, 47) + "...";
    }

    return title;
  };

  // ==========================================
  // API CALLS
  // ==========================================

  const fetchNotifications = useCallback(async (page = 1, append = false) => {
    if (!profile?.infinityCode) return;

    try {
      setLoading(true);
      const res = await apiFetch(`${BASE_URL}/api/notifications?page=${page}&limit=20`, { method: "GET" });
      if (!res.ok) throw new Error("Failed to fetch notifications");

      const data = await res.json();
      if (data.success) {
        const resolved = await resolveNamesAndMessages(data.data);
        setNotifications(prev => append ? [...prev, ...resolved.filter(n => !prev.some(p => p.id === n.id))] : resolved);
        setPagination(data.pagination);
        setHasLoaded(true);
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.infinityCode, resolveNamesAndMessages]);

  const fetchUnreadCount = useCallback(async () => {
    if (!profile?.infinityCode) return;
    try {
      const res = await apiFetch(`${BASE_URL}/api/notifications/unread-count`, { method: "GET" });
      const data = await res.json();
      if (data.success && typeof data.unreadCount === "number") {
        setUnreadCount(data.unreadCount);
      }
    } catch (err) {
      console.error("Failed to fetch unread count", err);
    }
  }, [profile?.infinityCode]);

  // Latest list, for handlers that look an item up without being re-created
  // on every change (the optimistic actions below and the socket events).
  const notificationsRef = useRef(notifications);
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Socket handlers are installed once per session; read the resolver
  // through a ref so a language switch also applies to later arrivals.
  const resolveRef = useRef(resolveNamesAndMessages);
  useEffect(() => {
    resolveRef.current = resolveNamesAndMessages;
  }, [resolveNamesAndMessages]);

  // The actions below are OPTIMISTIC: the UI changes the instant the user
  // acts and the request runs behind it. On failure the authoritative state
  // is re-pulled from the server instead of rolling back a snapshot, so a
  // socket event that landed in the meantime is never clobbered.
  const resyncFromServer = useCallback(() => {
    fetchUnreadCount();
    fetchNotifications(1, false);
  }, [fetchNotifications, fetchUnreadCount]);

  const markAsRead = async (id) => {
    const target = notificationsRef.current.find(n => n.id === id);
    if (target?.read) return;
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    if (target) setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      const res = await apiFetch(`${BASE_URL}/api/notifications/${id}/read`, { method: "PATCH" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error("Mark as read failed", err);
      resyncFromServer();
    }
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => n.read ? n : { ...n, read: true }));
    setUnreadCount(0);
    try {
      const res = await apiFetch(`${BASE_URL}/api/notifications/read-all`, { method: "PATCH" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error("Mark all as read failed", err);
      resyncFromServer();
    }
  };

  const deleteNotification = async (id) => {
    const target = notificationsRef.current.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (target && !target.read) setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      const res = await apiFetch(`${BASE_URL}/api/notifications/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error("Delete notification failed", err);
      resyncFromServer();
    }
  };

  const deleteAllNotifications = async () => {
    setNotifications([]);
    setUnreadCount(0);
    setPagination({ page: 1, hasMore: false, totalPages: 1 });
    try {
      const res = await apiFetch(`${BASE_URL}/api/notifications/delete-all`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error("Delete all notifications failed", err);
      resyncFromServer();
    }
  };

  // ==========================================
  // SOCKET.IO LOGIC
  // ==========================================

  const paginationRef = useRef(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    if (profileLoading || !profile?.infinityCode) return;
    fetchNotifications(1, false);
    fetchUnreadCount();
  }, [profile?.infinityCode, profileLoading, fetchNotifications, fetchUnreadCount]);

  useEffect(() => {
    if (profileLoading) return;

    if (!profile?.infinityCode) {
      setNotifications([]);
      setUnreadCount(0);
      setPagination({ page: 1, hasMore: false, totalPages: 1 });
      return;
    }

    // Fallback polling every 30 seconds
    const pollInterval = setInterval(() => {
      fetchUnreadCount();
      if (paginationRef.current.page === 1 && !isDropdownOpenRef.current) {
        fetchNotifications(1, false);
      }
    }, 30000);

    // Socket Connection
    const token = localStorage.getItem("token");
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
    });
    
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("🟢 Notifications Socket connected");
      setIsConnected(true);
      // Resync after every (re)connect: any notification/count events emitted
      // while this tab was disconnected were missed, so pull the authoritative
      // state from the server instead of trusting the last in-memory values.
      fetchUnreadCount();
      if (paginationRef.current.page === 1) {
        fetchNotifications(1, false);
      }
    });

    socket.on("notification:new", async (notification) => {
      console.log("📬 New notification received via socket:", notification);
      if (notification.bulk) {
        // Bulk insert, refetch both notifications and count
        fetchUnreadCount();
        if (!isDropdownOpenRef.current) {
          fetchNotifications(1, false);
        }
        return;
      }

      // Aggregated chat notifications: the backend updates one notification
      // in place per chat (e.g. "Mohamed sent a message" → "Mohamed and
      // Ahmed sent messages") instead of creating a new row. `isUpdate`
      // tells us which case this is — only a brand-new notification should
      // bump the unread badge; an update to an already-unread one must not
      // double-count it.
      if (!notification.isUpdate) {
        setUnreadCount(prev => prev + 1);
      }

      const [resolved] = await resolveRef.current([notification]);
      if (!resolved) return;
      // `_liveAt` marks a real-time arrival so the open list can flash it
      // once. New notifications AND in-place aggregate updates ("X" → "X and
      // Y") move to the top — an update is fresh activity. Filtering by id
      // first is also the duplicate guard for events re-delivered after a
      // reconnect.
      const live = { ...resolved, _liveAt: Date.now() };
      setNotifications(prev => [live, ...prev.filter(n => n.id !== live.id)]);
    });

    // The server's authoritative unread total for this user — the badge itself.
    socket.on("notification:count", (data) => {
      if (typeof data?.unreadCount === "number") setUnreadCount(data.unreadCount);
      else fetchUnreadCount();
    });

    // Read / deleted from ANOTHER tab or device of this account. A change this
    // tab made itself is already applied optimistically (the item is read or
    // gone here), so the badge only moves when it's still unread locally.
    socket.on("notification:read", (data) => {
      if (!data?.id) return;
      const target = notificationsRef.current.find(n => n.id === data.id);
      if (!target) {
        fetchUnreadCount(); // not loaded here (e.g. a later page) — ask the server
      } else if (!target.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.map(n => n.id === data.id ? { ...n, read: true } : n));
    });

    socket.on("notification:readAll", () => {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    });

    socket.on("notification:deleted", (data) => {
      if (!data?.id) return;
      const target = notificationsRef.current.find(n => n.id === data.id);
      if (target && !target.read) setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.filter(n => n.id !== data.id));
    });

    // Another tab/session of this user wiped all notifications — clear here
    // too so every open tab stays in sync (the count event that follows
    // resets the badge to 0).
    socket.on("notification:deletedAll", () => {
      setNotifications([]);
      setUnreadCount(0);
      setPagination({ page: 1, hasMore: false, totalPages: 1 });
    });

    // ── Bridge private-message events to a global CustomEvent ────────────────
    // The messenger page maintains its own separate Socket.IO connection in
    // useSocket.js. If that page hasn't been mounted (or its socket failed
    // silently while this one is up), DM events never reach UI state even
    // though the backend emits to this user's room. This always-on socket
    // re-broadcasts private-message events as `window` CustomEvents so
    // useSocket's effect can route them to the messenger handlers regardless
    // of which socket actually received the upstream event.
    const dispatchPrivateMessage = (name, detail) => {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    };
    // Superadmin broadcast announcements → components/Broadcasts/BroadcastCenter.
    socket.on("broadcast:new", (data) => {
      window.dispatchEvent(new CustomEvent("hrms:broadcast:new", { detail: data }));
    });
    socket.on("broadcast:revoked", (data) => {
      window.dispatchEvent(new CustomEvent("hrms:broadcast:revoked", { detail: data }));
    });
    socket.on("private_message:received", (data) => {
      dispatchPrivateMessage("hrms:private-message:received", data);
    });
    socket.on("private_message:sent", (data) => {
      dispatchPrivateMessage("hrms:private-message:sent", data);
    });
    socket.on("private_message:read", (data) => {
      dispatchPrivateMessage("hrms:private-message:read", data);
    });
    socket.on("private_message:typing", (data) => {
      dispatchPrivateMessage("hrms:private-message:typing", data);
    });
    socket.on("private_messages:read", (data) => {
      dispatchPrivateMessage("hrms:private-messages:read", data);
    });
    // I read a chat (in this tab or on another device) — clear its unread state.
    socket.on("private_messages:read_sync", (data) => {
      dispatchPrivateMessage("hrms:private-messages:read-sync", data);
    });
    socket.on("private_message:reaction", (data) => {
      dispatchPrivateMessage("hrms:private-message:reaction", data);
    });

    // ── Bridge chat-group events too ──────────────────────────────────────
    // Same rationale as the private-message bridge above: the messenger
    // page's own socket (useSocket.js) only listens to these while that page
    // is mounted. Re-broadcast on this always-on connection so any consumer
    // (e.g. the Mini Chat Dock) gets group realtime updates on every page.
    socket.on("chat_group:message", (data) => {
      dispatchPrivateMessage("hrms:chat_group:message", data);
    });
    socket.on("chat_group:reaction", (data) => {
      dispatchPrivateMessage("hrms:chat_group:reaction", data);
    });
    socket.on("chat_group:status_update", (data) => {
      dispatchPrivateMessage("hrms:chat_group:status_update", data);
    });
    socket.on("chat_group:message_deleted", (data) => {
      dispatchPrivateMessage("hrms:chat_group:message_deleted", data);
    });
    socket.on("chat_group:joined", (data) => {
      dispatchPrivateMessage("hrms:chat_group:joined", data);
    });
    socket.on("chat_group:members_added", (data) => {
      dispatchPrivateMessage("hrms:chat_group:members_added", data);
    });
    socket.on("chat_group:member_removed", (data) => {
      dispatchPrivateMessage("hrms:chat_group:member_removed", data);
    });
    socket.on("chat_group:removed", (data) => {
      dispatchPrivateMessage("hrms:chat_group:removed", data);
    });

    // ── Bridge presence (online/offline) to global CustomEvents ───────────────
    // Backend emits `users:online` (snapshot, array of infinityCodes on connect)
    // and `user:online` / `user:offline` (one infinityCode per event) globally.
    // The messenger page has its own socket subscribed to these, but the
    // messenger page is only mounted when the user is on it — without this
    // bridge, presence updates are missed when navigating elsewhere. Re-broadcast
    // on the always-on NotificationContext socket so any consumer (including
    // future global presence indicators) can listen via window events.
    socket.on("users:online", (data) => {
      // Refresh the cache so late-mounting consumers see current presence.
      presenceCache.clear();
      if (Array.isArray(data)) {
        for (const id of data) presenceCache.add(String(id));
      }
      dispatchPrivateMessage("hrms:users:online", data);
    });
    socket.on("user:online", (data) => {
      if (data?.userId != null) presenceCache.add(String(data.userId));
      dispatchPrivateMessage("hrms:user:online", data);
    });
    socket.on("user:offline", (data) => {
      if (data?.userId != null) presenceCache.delete(String(data.userId));
      dispatchPrivateMessage("hrms:user:offline", data);
    });
    socket.on("disconnect", () => {
      // Stale entries from a previous session shouldn't paint as online while
      // we reconnect. Server will send a fresh snapshot on reconnect.
      presenceCache.clear();
      setIsConnected(false);
    });

    // Explicit logout signal — AuthContext.logout dispatches this so we tear
    // down the socket immediately. Without this the connection stays open
    // (useProfile is non-reactive, so `profile?.infinityCode` doesn't flip
    // to undefined), the backend never fires its disconnect handler, and
    // the presence counter never reaches zero → peers don't see user:offline.
    const onLogout = () => {
      if (socketRef.current) {
        try { socketRef.current.disconnect(); } catch (_) { /* already closed */ }
        socketRef.current = null;
      }
      // Clear local presence too so a relog starts from a clean snapshot.
      presenceCache.clear();
      setIsConnected(false);
    };
    window.addEventListener("hrms:auth:logout", onLogout);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener("hrms:auth:logout", onLogout);
      if (socketRef.current) {
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [profile?.infinityCode, profileLoading]);

  // Handle Language changes rebuilding messages
  useEffect(() => {
    if (notifications.length === 0) return;
    resolveNamesAndMessages(notifications).then(resolved => {
      setNotifications(resolved);
    });
  }, [language]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        pagination,
        hasLoaded,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        deleteAllNotifications,
        isDropdownOpen,
        setIsDropdownOpen,
        notificationsDisabled,
        setNotificationsDisabled,
        miniChatDisabled,
        setMiniChatDisabled,
        clientBadgeDisabled,
        setClientBadgeDisabled,
        // Reuse this connection instead of opening a second socket elsewhere.
        socket: socketRef.current,
        isConnected,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
